import React, { SyntheticEvent } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { AircraftExtended } from "../../../Utils/TypeExtensions";
import { IntegerCustomControlProps } from "../../../Utils/Interfaces";
import { styled } from "@mui/system";
import { useLiveQuery } from "dexie-react-hooks";
import { TicketDatabase } from "../../../Utils/TicketDatabase";
import { CONTROL_MARGIN } from "../../../MuiThemes";
import { getHelperText, propertyHasErrors } from "../inputHelpers";

const GroupHeader = styled("div")(({ theme }) => ({
    position: "sticky",
    top: "-8px",
    padding: "4px 10px",
    color: theme.palette.primary.main,
    backgroundColor: "#DDDDDD",
    fontWeight: "bold",
}));

const GroupItems = styled("ul")({
    padding: 0,
    paddingLeft: 10,
});

export default function AircraftSelector(props: IntegerCustomControlProps): JSX.Element {
    const aircraftList = useLiveQuery(async () => TicketDatabase.aircraft.toArray());

    if (!aircraftList) return <></>;

    function handleOnChange(e: SyntheticEvent, value: string | AircraftExtended | null) {
        let newId;
        if (value !== null) {
            newId = (value as AircraftExtended).id;
        }

        props.mergeChanges(props.sourceId, { [props.sourceProperty]: newId });
    }

    let sortedByName = [] as AircraftExtended[];

    sortedByName = aircraftList
        .sort((a: AircraftExtended, b: AircraftExtended) => {
            const acTypeCompare = (a.AircraftType.sort_order ?? 0) - (b.AircraftType.sort_order ?? 0);
            return acTypeCompare === 0 ? (a.selectName ?? "").localeCompare(b.selectName ?? "") : acTypeCompare; // sort by type, then name
        })
        .filter((a: AircraftExtended) => a.selectName);

    const initialValue = sortedByName.find((p: AircraftExtended) => p.id === props.initialValue);

    return (
        <>
            <Autocomplete
                disabled={props.isDisabled}
                value={initialValue === undefined ? null : initialValue}
                options={sortedByName}
                onChange={handleOnChange}
                groupBy={(ac) => {
                    return ac.AircraftType.name ?? "";
                }}
                renderGroup={(params) => (
                    <li key={params.group}>
                        <GroupHeader>{params.group}</GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                    </li>
                )}
                getOptionLabel={(aircraft: AircraftExtended | string) =>
                    (aircraft as AircraftExtended).callsign === undefined ? aircraft.toString() : (aircraft as AircraftExtended).callsign ?? ""
                }
                filterSelectedOptions
                renderInput={(params) => (
                    <TextField
                        required={props.isRequired}
                        error={propertyHasErrors(props.errors, props.containerUrl, props.sourceId, props.sourceProperty)}
                        helperText={getHelperText(props.errors, props.containerUrl, props.sourceId, props.sourceProperty)}
                        {...params}
                        margin={CONTROL_MARGIN}
                        label={props.labelText}
                        placeholder="type to search"
                    />
                )}
            />
        </>
    );
}
